import {
  $ArithmeticOperation,
  $GymMode,
  $ModuleArea,
  ArithmeticOperation,
  GymMode,
  ModuleArea,
  SlugFor,
} from '@suntex.fim/enums';

import { IconKey } from '../../assets';
import { NodeModel } from '../../types';

// eslint-disable-next-line no-magic-numbers -- Numeric literals 0-11 define valid game indices in the type union
type GameIndex = 0 | 1 | 2 | 3 | 4 | 5 | 6 | 7 | 8 | 9 | 10 | 11;

// Icon name mappings for gym number icons
const FRACTION_ICONS = [
  '1-32',
  '1-16',
  '1-8',
  '1-5',
  '1-4',
  '1-3',
  '3-8',
  '1-2',
  '5-8',
  '2-3',
  '3-4',
  '7-8',
] as const;

const DECIMAL_ICONS = [
  '0-05',
  '0-1',
  '0-2',
  '0-25',
  '0-3',
  '0-4',
  '0-5',
  '0-6',
  '0-7',
  '0-75',
  '0-8',
  '0-9',
] as const;

const INTEGER_ICONS = [
  'neg-1',
  'neg-2',
  'neg-3',
  'neg-4',
  'neg-5',
  'neg-6',
  'neg-7',
  'neg-8',
  'neg-9',
  'neg-10',
  'neg-11',
  'neg-12',
] as const;

const WHOLE_NUMBER_ICONS = [
  '1',
  '2',
  '3',
  '4',
  '5',
  '6',
  '7',
  '8',
  '9',
  '10',
  '11',
  '12',
] as const;

export function buildGymModeNode(mode: GymMode): NodeModel {
  const gymMode = $GymMode.describe(mode);
  return {
    tag: gymMode.slug,
    icon:
      gymMode.value === GymMode.K2
        ? 'modules/gyms'
        : (`modes/${gymMode.slug}` as IconKey),
    label: {
      aria: gymMode.label,
    },
    filtered: {
      label: `${gymMode.label} Gyms`,
      icon:
        gymMode.value === GymMode.K2
          ? undefined
          : (`alts/gym-${gymMode.slug}` as IconKey),
      color: $ModuleArea.describe(ModuleArea.Gyms).color,
    },
    color: gymMode.color,
    layout: 'operations',
    children: $ArithmeticOperation
      .describeAll(
        mode === GymMode.K2
          ? [ArithmeticOperation.Addition, ArithmeticOperation.Subtraction]
          : undefined,
      )
      .map((op) => buildGymOpNode(gymMode, op)),
  };
}

function buildGymOpNode(
  gymMode: {
    value: GymMode;
    slug: SlugFor<typeof $GymMode>;
    label: string;
    color: number;
    steps: number;
  },
  op: {
    value: ArithmeticOperation;
    slug: SlugFor<typeof $ArithmeticOperation>;
    label: string;
    color: number;
  },
): NodeModel {
  const moduleSlug = `gym-${gymMode.slug}-${op.slug}` as const;
  const hrefBase = '/modules/gym';
  const children = [] as NodeModel[];

  if (moduleSlug === 'gym-k2-mul' || moduleSlug === 'gym-k2-div') {
    throw new Error(`Invalid gym mode ${gymMode.value} for ${op.value}`);
  }

  for (let gn = 1; gn <= gymMode.steps; gn++) {
    let numberIcon: NodeModel['icon'];
    const gameIndex = (gn - 1) as GameIndex;
    switch (gymMode.value) {
      case GymMode.Fractions:
        numberIcon = `gym-numbers/frc/${FRACTION_ICONS[gameIndex]}`;
        break;
      case GymMode.Decimals:
        numberIcon = `gym-numbers/dec/${DECIMAL_ICONS[gameIndex]}`;
        break;
      case GymMode.Integers:
        numberIcon = `gym-numbers/int/${INTEGER_ICONS[gameIndex]}`;
        break;
      default:
        numberIcon = `gym-numbers/num/${WHOLE_NUMBER_ICONS[gameIndex]}`;
        break;
    }
    children.push({
      href: `${hrefBase}/${gymMode.slug}/${op.slug}/${gn}`,
      dataPath: ['gyms', gymMode.slug, op.slug, `${gn}`],
      icon: numberIcon,
      label: {
        aria: `${gymMode.label} ${op.label} Gym #${gn}`,
      },
      color: gymMode.color,
      children: [],
    });
  }

  return {
    moduleSlug: moduleSlug,
    tag: op.slug,
    icon: `operations/${op.slug}`,
    label: {
      aria: op.label,
    },
    filtered: {
      label: `${gymMode.label}\n${op.label} Gyms`,
      icon: `alts/${moduleSlug}`,
      color: $ModuleArea.describe(ModuleArea.Gyms).color,
    },
    color: op.color,
    layout: 'star',
    centerOffset: { x: 25, y: 0 },
    children,
  };
}
